import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Country } from '@models/country.model';
import { CountryCity } from '@models/countryCity.model';
import { Currency } from '@models/currency.model';
import { Industry } from '@models/industry.model';
import { Location } from '@models/location.model';
import { Lookup } from '@models/lookup.model';
import { Region } from '@models/region.model';
import { VendorType } from '@models/vendorType.model';
import * as Constants from '../shared/_constants';
import { LoggingService } from '@services/logging.service';
import { FunctionType } from '@models/functionType.model';
import { KeyValuePair } from '@models/keyValuePair.model';
import { RegionCountry } from '@models/regioncountry.model';
import { Role } from '@models/role.model';
import { IndustryCode } from '../models/industryCode.model';
import { IndustryType } from '../models/industryType.model';
import { AdjustmentLookup, LookupType, AdjustmentInterestRateVariantLookup } from '../mystudies/_study_constants';
import { VendorAccount } from '../models/vendoraccount.model';
import { Ratio } from '../models/ratio.model';
import { VendorAccountMapping } from '@models/vendorAccountMapping.model';
import { Cacheable } from 'ts-cacheable';
import { Source } from '../models/source.model';
import { RatioVendorAccountMapping } from '@models/ratioVendorAccountMapping.model';
import * as _ from 'lodash-es';
import { firstValueFrom, Observable, of } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { IndustryTypeDatabase } from '@models/IndustryTypeDatabase.model';

/*
    This service is intended to contain all lookups that are used for things such as drop downs etc.
    These are values that are typically not updated within the UI but used to set ids/lookups on other things such as studies
*/
@Injectable()
export class LookupService {
  baseUrl: string;
  public viewResultsCount: Lookup = new Lookup();

  // store list items after retrieved and processed
  private lookupsMap: Map<string, any[]> = new Map<string, any[]>();

  constructor(private http: HttpClient, private logger: LoggingService) {}

  public async GetCities(): Promise<CountryCity[]> {
    return firstValueFrom(this.GetCities$());
  }

  public GetCities$(): Observable<CountryCity[]> {
    return this.GetValues$(`CountryCity`) as Observable<CountryCity[]>;
  }

  public async GetCitiesByCountry(countryId: number): Promise<CountryCity[]> {
    return this.GetValues<CountryCity>('CityByCountry', countryId);
  }

  public async GetProjectOfficeCountries(): Promise<Country[]> {
    return firstValueFrom(this.GetProjectOfficeCountries$());
  }

  public GetProjectOfficeCountries$(): Observable<Country[]> {
    return this.GetValues$(`ProjectOfficeCountry`) as Observable<Country[]>;
  }

  public async GetTestedPartyCountries(): Promise<Country[]> {
    return firstValueFrom(this.GetTestedPartyCountries$());
  }

  public GetTestedPartyCountries$(): Observable<Country[]> {
    return this.GetValues$(`TestedPartyCountry`) as Observable<Country[]>;
  }

  public async GetCurrencies(): Promise<Currency[]> {
    return firstValueFrom(this.GetCurrencies$());
  }

  public GetCurrencies$(): Observable<Currency[]> {
    return this.GetValues$(`Currency`) as Observable<Currency[]>;
  }

  public async GetCurrencyUnits(): Promise<Lookup[]> {
    return firstValueFrom(this.GetCurrencyUnits$());
  }

  public GetCurrencyUnits$(): Observable<Lookup[]> {
    return this.GetValues$(`Lookup/CurrencyUnit`) as Observable<Lookup[]>;
  }

  public async GetIndustryTypes(): Promise<Lookup[]> {
    return firstValueFrom(this.GetIndustryTypes$());
  }

  public GetIndustryTypes$(): Observable<Lookup[]> {
    return this.GetValues$(`Lookup/IndustryType`) as Observable<Lookup[]>;
  }

  public async GetLocations(): Promise<Location[]> {
    return firstValueFrom(this.GetLocations$());
  }

  public GetLocations$(): Observable<Location[]> {
    return this.GetValues$(`Location`) as Observable<Location[]>;
  }

  public async GetIndustries(): Promise<Industry[]> {
    return firstValueFrom(this.GetIndustries$());
  }

  public GetIndustries$(): Observable<Industry[]> {
    return this.GetValues$(`Industry`) as Observable<Industry[]>;
  }

  public async GetRegions(isUsedForFilter = false): Promise<Region[]> {
    return firstValueFrom(this.GetRegions$(isUsedForFilter));
  }

  public GetRegions$(isUsedForFilter = false): Observable<Region[]> {
    return this.GetValues$(`Region/${isUsedForFilter}`) as Observable<Region[]>;
  }

  public async GetVendorTypes(): Promise<VendorType[]> {
    return firstValueFrom(this.GetVendorTypes$());
  }

  public GetVendorTypes$(): Observable<VendorType[]> {
    return this.GetValues$(`VendorType`) as Observable<VendorType[]>;
  }

  public async GetFunctionTypes(): Promise<FunctionType[]> {
    return firstValueFrom(this.GetFunctionTypes$());
  }

  public GetFunctionTypes$(): Observable<FunctionType[]> {
    return this.GetValues$(`Function`) as Observable<FunctionType[]>;
  }

  public async GetCountryByRegion(): Promise<RegionCountry[]> {
    return firstValueFrom(this.GetCountryByRegion$());
  }

  public GetCountryByRegion$(): Observable<RegionCountry[]> {
    return this.GetValues$(`'CountryByRegion', regionId`) as Observable<RegionCountry[]>;
  }

  public async GetAllRole(): Promise<Role[]> {
    return firstValueFrom(this.GetAllRole$());
  }

  public GetAllRole$(): Observable<Role[]> {
    return this.GetValues$(`Role`) as Observable<Role[]>;
  }

  public async GetAllRegionCountries(): Promise<RegionCountry[]> {
    return firstValueFrom(this.GetAllRegionCountries$());
  }

  public GetAllRegionCountries$(): Observable<RegionCountry[]> {
    return this.GetValues$<RegionCountry>(`RegionCountry`) as Observable<RegionCountry[]>;
  }

  public async GetAllActiveIndustryCode(industryTypeId: number): Promise<IndustryCode[]> {
    return firstValueFrom(this.GetAllActiveIndustryCode$(industryTypeId));
  }

  public GetAllActiveIndustryCode$(industryTypeId: number): Observable<IndustryCode[]> {
    return this.GetValues$('IndustryCodes', industryTypeId) as Observable<IndustryCode[]>;
  }

  public GetAllActiveIndustryType$(): Observable<IndustryType[]> {
    return this.GetValues$(`IndustryTypes`) as Observable<IndustryType[]>;
  }

  public GetIndustryTypeByDatabaseId$(databaseIds: number[]): Observable<IndustryTypeDatabase[]> {
    const ids = _.join(databaseIds, '&dbIds=');
    return this.GetValues$(`IndustryTypesByDatabaseId?dbIds=${ids}`) as Observable<IndustryTypeDatabase[]>;
  }

  public async GetStudyStatuses(): Promise<Lookup[]> {
    return firstValueFrom(this.GetStudyStatuses$());
  }

  public GetStudyStatuses$(): Observable<Lookup[]> {
    return this.GetValues$(`Lookup/StudyStatus`) as Observable<Lookup[]>;
  }

  public async GetAllFinancialRatio(): Promise<Ratio[]> {
    return firstValueFrom(this.getAllFinancialRatio$());
  }

  public getAllFinancialRatio$(): Observable<Ratio[]> {
    return this.GetValues$(`FinancialRatio`) as Observable<Ratio[]>;
  }

  public async GetAllTurnoverRange(): Promise<Lookup[]> {
    return firstValueFrom(this.GetAllTurnoverRange$());
  }

  public GetAllTurnoverRange$(): Observable<Lookup[]> {
    return this.GetValues$(`Lookup/TurnoverRange`) as Observable<Lookup[]>;
  }

  public getFilterVendorAccount$(): Observable<VendorAccount[]> {
    return this.GetValues$<VendorAccount>(`FilterVendorAccount`);
  }

  public async GetVendorAccountWithCategory(isOtherCategoryIncluded = false): Promise<VendorAccountMapping[]> {
    return firstValueFrom(this.GetVendorAccountWithCategory$(isOtherCategoryIncluded));
  }

  public GetVendorAccountWithCategory$(isOtherCategoryIncluded = false): Observable<VendorAccountMapping[]> {
    return this.GetValues$<VendorAccountMapping>(`VendorAccountWithCategory/${isOtherCategoryIncluded}`) as Observable<VendorAccountMapping[]>;
  }

  public async GetLookupValues(lookupType: LookupType): Promise<Lookup[]> {
    // As lookuptypeid  is coming in parameter, but we need string data to be pass to web api.
    const lookupTypeText = LookupType[lookupType];

    return firstValueFrom(this.GetLookupValues$(lookupTypeText));
  }

  public GetLookupValues$(lookupTypeText: string): Observable<Lookup[]> {
    return this.GetValues$<Lookup>(`Lookup/${lookupTypeText}`) as Observable<Lookup[]>;
  }

  public GetLookupValueList$(lookupType: LookupType): Observable<Lookup[]> {
    const lookupTypeText = LookupType[lookupType];
    return this.GetValues$<Lookup>(`Lookup/${lookupTypeText}`) as Observable<Lookup[]>;
  }

  public async GetCompanySize(): Promise<Lookup[]> {
    return firstValueFrom(this.GetCompanySize$());
  }

  public GetCompanySize$(): Observable<Lookup[]> {
    return this.GetValues$<Lookup>(`Lookup/CompanySize`) as Observable<Lookup[]>;
  }

  public async GetCompanyStatus(): Promise<Lookup[]> {
    return firstValueFrom(this.GetCompanyStatus$());
  }

  public GetCompanyStatus$(): Observable<Lookup[]> {
    return this.GetValues$<Lookup>(`Lookup/CompanyStatus`) as Observable<Lookup[]>;
  }

  public async GetLegalForm(): Promise<Lookup[]> {
    return firstValueFrom(this.GetLegalForm$());
  }

  public GetLegalForm$(): Observable<Lookup[]> {
    return this.GetValues$<Lookup>(`Lookup/LegalForm`) as Observable<Lookup[]>;
  }

  public async GetAccountType(studySearchStrategyID: number): Promise<Lookup[]> {
    return firstValueFrom(this.GetAccountType$(studySearchStrategyID));
  }

  public GetAccountType$(studySearchStrategyID: number): Observable<Lookup[]> {
    return this.GetValues$<Lookup>(`FinancialTypeFilter/${studySearchStrategyID}`) as Observable<Lookup[]>;
  }

  public getListingStatuses$(): Observable<Lookup[]> {
    return this.GetValues$<Lookup>(`Lookup/ListingStatus`) as Observable<Lookup[]>;
  }

  public async GetShareholderCharacteristics(): Promise<Lookup[]> {
    return firstValueFrom(this.GetShareholderCharacteristics$());
  }

  public GetShareholderCharacteristics$(): Observable<Lookup[]> {
    return this.GetValues$<Lookup>(`Lookup/ShareholderType`) as Observable<Lookup[]>;
  }

  public async GetSubsidiaryCharacteristics(): Promise<Lookup[]> {
    return firstValueFrom(this.GetSubsidiaryCharacteristics$());
  }

  public GetSubsidiaryCharacteristics$(): Observable<Lookup[]> {
    return this.GetValues$<Lookup>(`Lookup/SubsidiaryType`) as Observable<Lookup[]>;
  }

  public async GetBvdIndependenceIndicator(): Promise<Lookup[]> {
    return firstValueFrom(this.GetBvdIndependenceIndicator$());
  }

  public GetBvdIndependenceIndicator$(): Observable<Lookup[]> {
    return this.GetValues$<Lookup>(`Lookup/BVDIndependenceIndicator`) as Observable<Lookup[]>;
  }

  public async GetAllAveragingMethods(): Promise<Lookup[]> {
    return firstValueFrom(this.GetAllAveragingMethods$());
  }

  public GetAllAveragingMethods$(): Observable<Lookup[]> {
    return this.GetValues$<Lookup>(`Lookup/AveragingMethod`) as Observable<Lookup[]>;
  }

  public async getAllSource(): Promise<Source[]> {
    return firstValueFrom(this.getAllSource$());
  }

  public getAllSource$(): Observable<Source[]> {
    return this.GetValues$<Source>(`GetSource`) as Observable<Source[]>;
  }

  public async GetViewResults() {
    this.GetViewResultsCount().subscribe((data) => {
      this.viewResultsCount.value = data.value;
      this.viewResultsCount.displayOrder = data.displayOrder;
      this.viewResultsCount.displayText = data.displayText;
      this.viewResultsCount.name = data.name;
    });
  }

  @Cacheable()
  public GetViewResultsCount() {
    this.logger.info(this.constructor.name, 'GetViewResultsCount', 'Start');
    const result = this.http.get<Lookup>('/api/master/GetLookupById/' + Constants.ViewResultCount.SearchCountId);
    return result;
  }

  // Added this method to clear the lookup/ filters cached data, need to call incase when we need to repopulate the master values.
  public clearlookupsMapCachedData() {
    this.logger.info(this.constructor.name, 'CleanlookupsMap', 'Start');
    this.lookupsMap.clear();
  }

  /* deprecated(MK | 23-04-2020): use GetValues$ instead */
  private async GetValues<T>(endpoint: string, id: number = null): Promise<T[]> {
    return firstValueFrom(this.GetValues$(endpoint, id)) as Promise<T[]>;
  }

  private GetValues$<T>(endpoint: string, id: number = null): Observable<T[]> {
    const mapKey = `${endpoint}${id}`;
    const collection = this.lookupsMap.get(mapKey);

    if (collection === undefined) {
      let url = `/api/master/${endpoint}`;
      if (id !== null) {
        url = `${url}/${id}`;
      }

      return this.http.get<T[]>(url).pipe(
        tap((lookupCollection) => {
          const colLength = _.isEmpty(collection) ? 0 : lookupCollection.length;
          this.logger.info(this.constructor.name, 'GetValues', `From API : [Url=${url},Count=${colLength}]`);
          return this.lookupsMap.set(mapKey, lookupCollection);
        })
      );
    } else {
      const colLength = _.isEmpty(collection) ? 0 : collection.length;
      this.logger.info(this.constructor.name, 'GetValues', ` From Map : [Count=${colLength}]`);
      return of(collection);
    }
  }

  // TODO: As new year will be added after year change, need to handle old year as that will be get discarded.
  // For Example: if 2009 selected in 2018 yr, 2009 will be get discarded as 2019 year will be get added.
  public getYearList(length: number): number[] {
    const years: number[] = [];
    const year = new Date().getFullYear();
    for (let i = year; i > year - length; i--) {
      years.push(i);
    }
    return years;
  }
  public getYearList$(length: number): Observable<number[]> {
    const years: number[] = [];
    const year = new Date().getFullYear();
    for (let i = year; i > year - length; i--) {
      years.push(i);
    }

    return of(years);
  }

  public getStudyYearList(length: number): number[] {
    const years: number[] = [];
    const year = new Date().getFullYear();
    for (let i = year; i >= length; i--) {
      years.push(i);
    }
    return years;
  }

  public getSequentialNumberList(start: number, length: number): number[] {
    const numbers: number[] = [];
    for (let i = start; i < start + length; i++) {
      numbers.push(i);
    }
    return numbers;
  }

  // TODO: As new year will be added after year change, need to handle old year as that will be get discarded.
  // For Example: if 2009 selected in 2018 yr, 2009 will be get discarded as 2019 year will be get added.
  public getFiscalYears(): KeyValuePair<number, string>[] {
    const fiscalYears: KeyValuePair<number, string>[] = [];

    const year = new Date().getFullYear();
    for (let i = year; i > year - 10; i--) {
      fiscalYears.push({ key: i, value: `FY${i.toString().substr(2, 2)}` });
    }
    return fiscalYears;
  }
  public getFiscalYears$(): Observable<KeyValuePair<number, string>[]> {
    const fiscalYears: KeyValuePair<number, string>[] = [];

    const year = new Date().getFullYear();
    for (let i = year; i > year - 10; i--) {
      fiscalYears.push({ key: i, value: `FY${i.toString().substr(2, 2)}` });
    }
    return of(fiscalYears);
  }

  /* deprecated(MK | 23-04-2018) use GetCompanyCategoryAccessType$ instead */
  public async GetCompanyCategoryAccessType(studySearchStrategyId: number): Promise<number> {
    this.logger.info(this.constructor.name, 'GetCompanyCategoryAccessType', 'Start');
    return firstValueFrom(this.GetCompanyCategoryAccessType$(studySearchStrategyId));
  }

  public GetCompanyCategoryAccessType$(studySearchStrategyId: number): Observable<number> {
    this.logger.info(this.constructor.name, 'GetCompanyCategoryAccessType', 'Start');
    return this.http.get<number>(`/api/study/GetCompanyCategoryAccessType/${studySearchStrategyId}`);
  }

  public async HasFullAccessForUserLicensingCountry(): Promise<boolean> {
    this.logger.info(this.constructor.name, 'HasFullAccessForUserLicensingCountry', 'Start');

    const applicationAccess = <boolean>(
      await firstValueFrom(this.http.get<boolean>(`/api/study/HasFullAccessForUserLicensingCountry`)).catch((e) =>
        this.logger.httpError(this.constructor.name, 'HasFullAccessForUserLicensingCountry', e)
      )
    );

    return applicationAccess;
  }
  public async isImpersonationEnabled(): Promise<boolean> {
    const accessType = <boolean>(
      await firstValueFrom(this.http.get<boolean>(`/api/master/IsImpersonationEnabled`)).catch((e) =>
        this.logger.httpError(this.constructor.name, 'IsImpersonationEnabled', e)
      )
    );

    this.logger.info(this.constructor.name, 'IsImpersonationEnabled', `accessType=${accessType}`);

    return accessType;
  }

  public async GetPLIRatioVendorAccountMapping(): Promise<RatioVendorAccountMapping[]> {
    return this.GetValues<RatioVendorAccountMapping>(`PLIRatioVendorAccountMapping`);
  }

  public getAdjustmentTypes$(): Observable<Lookup[]> {
    return this.GetLookupValues$(AdjustmentLookup[AdjustmentLookup.AdjustmentType]);
  }

  public getAdjustmentInterestRateVariants$(): Observable<Lookup[]> {
    return this.GetLookupValues$(AdjustmentInterestRateVariantLookup[AdjustmentInterestRateVariantLookup.AdjustmentInterestRateVariant]);
  }

  public isNonBvdDatabase(selectedDatabaseIds: number[]): Observable<boolean> {
    let databaseIds: string[] = [];
    databaseIds = selectedDatabaseIds.map(String);
    return this.GetLookupValues$(LookupType[LookupType.NonBvdDatabaseType]).pipe(
      map((DbTypelookup) => DbTypelookup.filter((lookupItem) => databaseIds.indexOf(lookupItem.value) != -1).length > 0)
    );
  }

  public GetEligiblePLIForDefaultBaseCalculationSetting(): Observable<number[]> {
    this.logger.info(this.constructor.name, 'GetEligiblePLIForDefaultBaseCalculationSetting', 'Start');
    return this.http.get<number[]>('/api/master/GetEligiblePLIForDefaultBaseCalculationSetting/');
  }
}
